/**
 * Blog listing page
 */

import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface BlogPost {
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  slug: string;
}

const blogPosts: BlogPost[] = [
  {
    title: '10 Ways AI Meeting Assistants Transform Productivity',
    excerpt: 'Discover how AI-powered meeting assistants are revolutionizing the way teams collaborate and work.',
    author: 'Sarah Johnson',
    date: '2025-01-15',
    category: 'Productivity',
    slug: 'ai-meeting-assistants-productivity',
  },
  {
    title: 'The Future of Remote Work: Meeting Intelligence',
    excerpt: 'How meeting intelligence tools are shaping the future of distributed teams and remote collaboration.',
    author: 'Michael Chen',
    date: '2025-01-10',
    category: 'Remote Work',
    slug: 'future-remote-work-meeting-intelligence',
  },
  {
    title: 'Getting Started with VoxaPilot: A Complete Guide',
    excerpt: 'Everything you need to know to get started with VoxaPilot and maximize your meeting productivity.',
    author: 'Emily Rodriguez',
    date: '2025-01-05',
    category: 'Guides',
    slug: 'getting-started-voxapilot-guide',
  },
];

const categories = ['All', 'Productivity', 'Remote Work', 'Guides', 'Product Updates'];

export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const {ref: headerRef, isVisible: headerVisible} = useScrollAnimation();
  const {ref: filtersRef, isVisible: filtersVisible} = useScrollAnimation();
  const {ref: gridRef, isVisible: gridVisible} = useScrollAnimation();

  const filteredPosts =
    selectedCategory === 'All'
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <SectionHeader
              title="Resources & Blog"
              subtitle="Insights, guides, and updates from the VoxaPilot team"
              centered
            />
          </div>

          {/* Category Filters */}
          <div
            ref={filtersRef as React.RefObject<HTMLDivElement>}
            className={`flex flex-wrap justify-center gap-2 mb-12 transition-all duration-700 ${
              filtersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: '200ms'}}
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`
                  px-4 py-2 rounded-md text-sm font-medium transition-all
                  ${
                    selectedCategory === category
                      ? 'bg-accent dark:bg-orange-600 text-white'
                      : 'bg-background dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-backgroundSecondary dark:hover:bg-[#2D2D30] border border-border dark:border-gray-700'
                  }
                `}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Grid */}
          <div ref={gridRef as React.RefObject<HTMLDivElement>} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <div
                key={post.slug}
                className={`transition-all duration-700 ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{transitionDelay: `${index * 0.1}s`}}
              >
                <Link to={ROUTES.BLOG_POST(post.slug)}>
                  <Card hover className="h-full flex flex-col">
                    <div className="aspect-video bg-gradient-to-br from-backgroundSecondary to-background rounded-t-lg" />
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="mb-2">
                        <span className="text-xs text-accent dark:text-orange-500 font-medium">{post.category}</span>
                      </div>
                      <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC] mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585] mb-4 flex-1 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-textTertiary dark:text-[#6A6A6A] pt-4 border-t border-border dark:border-gray-700">
                        <span>{post.author}</span>
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}


